import { 
  createEntityAdapter, 
  createSlice, 
  EntityAdapter, 
  EntityId, 
  EntityState, 
  PayloadAction, 
  Slice,
  SliceCaseReducers,
  SliceSelectors,
} from "@reduxjs/toolkit";

import { ValueType } from "../../Definitions"
import { isEqual } from "lodash";
import { EntityWithId, StoreReducerFactory } from "../storeReducerFactory";

const OPTION_TYPES = {
  animal: "animal",
  system: "system",
  section: "section",
  milestone: "milestone",
  group: "group",
  feedCurve: "feedCurve",
  location: "location"
} as const;

export type OptionType = typeof OPTION_TYPES[keyof typeof OPTION_TYPES];


export class OptionsFactory<ReducerName extends OptionType, H extends string, V extends EntityWithId<H>>
extends StoreReducerFactory<ReducerName, H, V> {
  protected initialize(reducerName: ReducerName,idKey: H): { entityAdapter: EntityAdapter<V, EntityId>; reducerSlice: Slice<EntityState<V, EntityId>, SliceCaseReducers<EntityState<V, EntityId>>, string, string, SliceSelectors<EntityState<V, EntityId>>>; } {
    const optionsEntityAdapter = createEntityAdapter<V, EntityId>({
      selectId: (option) => option[idKey]
    });
    const { selectAll } = optionsEntityAdapter.getSelectors();
    const optionsSlice = createSlice<EntityState<V,EntityId>, any, `${OptionType}Options`,SliceSelectors<EntityState<V,EntityId>>>({
      name: `${reducerName}Options`,
      initialState: optionsEntityAdapter.getInitialState({lastFetched: undefined}),
      reducers: {
        clear: (state: EntityState<V, EntityId>) => {
          optionsEntityAdapter.removeAll(state);
          if ("lastFetched" in state) {
            state.lastFetched = undefined;
          }
        },
        upsert: (state: EntityState<V, EntityId>, { payload }: PayloadAction<V[]>) => {
          const existing = selectAll(state);
          if (existing.length !== payload.length || !isEqual(existing, payload)) {
            optionsEntityAdapter.upsertMany(state, payload);
          }
          if ("lastFetched" in state) {
            state.lastFetched = Date.now();
          }
        },
        remove: optionsEntityAdapter.removeMany,
        updateMany: optionsEntityAdapter.updateMany,
      }
    });
    return {entityAdapter: optionsEntityAdapter, reducerSlice: optionsSlice};
    
  }
}

export function createOptionsStoreEntry(optionsName: OptionType): StoreReducerFactory<typeof optionsName, "id", ValueType> {
  return new OptionsFactory(optionsName, "id");
}